import { NO_REASON_CODE } from '../../types/creditNotes';

/**
 * The five reason GROUPS the charts stack, plus "no reason recorded".
 *
 * WHY GROUPS AT ALL. Eleven bands in a monthly stack is not a chart anybody reads. The ten codes
 * keep their own bar in the ranking below, where a long list is fine; only the stack is grouped.
 *
 * WHY THIS GROUPING IS NOT INVENTED VOCABULARY. P4 refused to create a "sub-reason" list because
 * none existed anywhere. This is the opposite case: the grouping is READ OFF the codes the
 * business already numbered — the three `H CLAIM …` values, the two `TRANSPORT …` values, the two
 * `WAREN …` values. It is a way of drawing the ten codes, not an eleventh business concept, and
 * the ranking underneath always shows the ungrouped truth.
 *
 * AN UNKNOWN CODE GETS ITS OWN VISIBLE BUCKET. If an eleventh catalogue row appears, it lands in
 * "Not grouped yet" rather than being folded into "Internal / other" — the same choice the audit
 * trail makes with an unknown column name. A bucket that looks unfinished gets fixed; a value
 * quietly absorbed into a neighbour does not.
 */

export interface ReasonGroup {
    id: string;
    label: string;
    /**
     * Validated with the dataviz palette validator (light and dark, categorical, adjacent-pair
     * CVD): all six checks pass in this order. The order is the stack order, so the pairs the
     * validator checked are the pairs a reader actually sees touching.
     */
    color: string;
}

export const REASON_GROUPS: ReasonGroup[] = [
    { id: 'CLAIMS', label: 'Claims', color: '#2563eb' },
    { id: 'GOODS', label: 'Goods return', color: '#c2410c' },
    { id: 'TRANSPORT', label: 'Transport', color: '#0d9488' },
    { id: 'BONUS', label: 'Volume bonus', color: '#9333ea' },
    { id: 'INTERNAL', label: 'Internal / other', color: '#a16207' },
    { id: 'UNGROUPED', label: 'Not grouped yet', color: '#db2777' },
    /**
     * Neutral by intent, not by oversight. "No reason recorded" is the ABSENCE of a category, and
     * giving it a hue of its own would let it read as a sixth kind of credit note. It is also the
     * band this page exists to make visible — at dit it is the whole column — so it keeps a
     * legend entry, a direct label and the same 2px gap as every other segment.
     */
    { id: NO_REASON_CODE, label: 'No reason recorded', color: '#9ca3af' },
];

const GROUP_BY_CODE: Record<string, string> = {
    CLAIM_REPLACE: 'CLAIMS',
    CLAIM_DISCOUNT: 'CLAIMS',
    CLAIM_DAMAGES: 'CLAIMS',
    GOODS_RETURN: 'GOODS',
    RESALE: 'GOODS',
    TRANSPORT_OWN: 'TRANSPORT',
    TRANSPORT_FWD: 'TRANSPORT',
    VOLUME_BONUS: 'BONUS',
    INTERNAL: 'INTERNAL',
    OTHER: 'INTERNAL',
};

export function groupOf(reasonCode: string): string {
    if (reasonCode === NO_REASON_CODE) return NO_REASON_CODE;
    return GROUP_BY_CODE[reasonCode] ?? 'UNGROUPED';
}

export function groupColor(reasonCode: string): string {
    const id = groupOf(reasonCode);
    return REASON_GROUPS.find(g => g.id === id)?.color ?? '#9ca3af';
}

export function groupLabel(groupId: string): string {
    return REASON_GROUPS.find(g => g.id === groupId)?.label ?? groupId;
}
